import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight, Check, Star,
  MessageSquare, Calendar, BarChart3, Shield, Zap,
  Globe, Play, TrendingUp, Lock, Headphones,
  Mail, FileText, Send, X, Bot, Sparkles, Heart,
  ChevronLeft, ChevronRight, DollarSign, Smile, Filter,
  Wrench, Scale, Stethoscope, Home, Wind, Scissors, Dumbbell,
  BookOpen, Clock, Users, Award,
} from 'lucide-react';
import { PublicHeader } from '@/components/common/PublicHeader';
import { PublicFooter } from '@/components/common/PublicFooter';
import PromotionalPopup from '@/components/common/PromotionalPopup';
import api from '@/services/api';
import { getVariant, trackAbEvent } from '@/utils/abTest';

const HERO_IMG = 'https://images.unsplash.com/photo-1673515324999-10eeadd359d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGJ1c2luZXNzJTIwb3duZXIlMjB3b3JraW5nJTIwbGFwdG9wJTIwY29mZmVlJTIwc2hvcHxlbnwwfHx8fDE3NzM1MTIwNzN8MA&ixlib=rb-4.1.0&q=85';

/* ─── Hero Carousel Slides — unified two-tier CTA ("Start Free" + "Book a Demo") ─── */
const HERO_SLIDES = [
  {
    id: 'never-miss',
    eyebrowIcon: Smile,
    eyebrow: 'Never miss a client',
    title: 'Stop losing clients to',
    titleHighlight: 'unanswered messages',
    body: 'Harmonix responds to every client instantly — across WhatsApp, website, email, and social media. It books appointments, qualifies leads, and follows up automatically.',
    ctaPrimary: 'Start Free',
    ctaSecondary: 'Book a Demo',
    image: '/images/hero/slide-1-never-miss.png',
    accent: 'from-teal-500 to-cyan-500',
    badgeColor: 'bg-teal-50 text-teal-700 border-teal-200',
    overlayLabel: 'New Lead Captured',
    overlaySub: 'AI booked appointment for tomorrow at 2:00 PM',
    overlayIcon: MessageSquare,
  },
  {
    id: 'multichannel',
    eyebrowIcon: Globe,
    eyebrow: 'One AI, every channel',
    title: 'Every channel,',
    titleHighlight: 'one assistant',
    body: 'Customers reach out from WhatsApp, Instagram, Facebook, email, and your website. Harmonix unifies them into one consistent voice — so your brand never feels fragmented and no message is ever missed.',
    ctaPrimary: 'Start Free',
    ctaSecondary: 'Book a Demo',
    image: '/images/hero/slide-5-multichannel.png',
    accent: 'from-rose-500 to-orange-500',
    badgeColor: 'bg-rose-50 text-rose-700 border-rose-200',
    overlayLabel: '5 channels, 1 inbox',
    overlaySub: '24/7 unified responses',
    overlayIcon: MessageSquare,
  },
  {
    id: 'support',
    eyebrowIcon: DollarSign,
    eyebrow: 'Cut support costs',
    title: 'Repetitive tickets',
    titleHighlight: 'auto-replied & routed',
    body: 'Most support questions are repeats. Harmonix answers them in seconds — and intelligently routes complex ones to the right human, slashing response time and headcount needs.',
    ctaPrimary: 'Start Free',
    ctaSecondary: 'Book a Demo',
    image: '/images/hero/slide-2-support-tickets.png',
    accent: 'from-orange-500 to-amber-500',
    badgeColor: 'bg-amber-50 text-amber-700 border-amber-200',
    overlayLabel: 'Tier-1 ticket resolved',
    overlaySub: 'Avg first response: 4 seconds',
    overlayIcon: Headphones,
  },
  {
    id: 'booking',
    eyebrowIcon: Calendar,
    eyebrow: 'Win clients while you sleep',
    title: 'Bookings happen',
    titleHighlight: 'while you sleep',
    body: 'Your AI books appointments at 2 AM with the same precision as 2 PM. Synced to Google Calendar, with smart conflict checks and instant confirmations — turning every late-night visitor into a paying customer.',
    ctaPrimary: 'Start Free',
    ctaSecondary: 'Book a Demo',
    image: '/images/hero/slide-3-247-booking.png',
    accent: 'from-violet-500 to-fuchsia-500',
    badgeColor: 'bg-violet-50 text-violet-700 border-violet-200',
    overlayLabel: 'Booked at 2:14 AM',
    overlaySub: '3 appointments captured overnight',
    overlayIcon: Calendar,
  },
  {
    id: 'qualified-leads',
    eyebrowIcon: Filter,
    eyebrow: 'Stronger sales funnel',
    title: 'Qualified leads,',
    titleHighlight: 'not noisy inboxes',
    body: 'Stop drowning in tire-kickers. Harmonix qualifies every inquiry against your criteria — budget, timeline, fit — and pushes only sales-ready leads to your team. Higher conversion. Less wasted time.',
    ctaPrimary: 'Start Free',
    ctaSecondary: 'Book a Demo',
    image: '/images/hero/slide-4-qualified-leads.png',
    accent: 'from-emerald-500 to-teal-500',
    badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    overlayLabel: '12 qualified leads today',
    overlaySub: '85% relevance score',
    overlayIcon: TrendingUp,
  },
  {
    id: 'employee-of-the-month',
    eyebrowIcon: Sparkles,
    eyebrow: 'Meet your AI Employees',
    title: 'Tireless. Trained.',
    titleHighlight: 'Always on shift.',
    body: 'Harmonix gives every channel its own dedicated AI Employee — fluent in your business, trained on your knowledge, and clocked-in 24/7. They greet customers, answer questions, book appointments, and never call in sick.',
    ctaPrimary: 'Start Free',
    ctaSecondary: 'Book a Demo',
    image: '/images/hero/slide-6-employee-of-the-month.png',
    accent: 'from-amber-500 to-orange-500',
    badgeColor: 'bg-amber-50 text-amber-700 border-amber-200',
    overlayLabel: 'AI Employee of the Month',
    overlaySub: 'Powered by Harmonix',
    overlayIcon: Award,
  },
];

const INDUSTRIES = [
  { name: 'Plumbing',     path: '/ai-for-plumbers',    icon: Wrench,         tagline: 'Never miss an emergency call',         iconColor: 'text-sky-600',      bgColor: 'bg-sky-50 border-sky-200' },
  { name: 'Law Firms',    path: '/ai-for-lawyers',     icon: Scale,          tagline: 'Professional client intake 24/7',      iconColor: 'text-slate-700',    bgColor: 'bg-slate-100 border-slate-200' },
  { name: 'Clinics',      path: '/ai-for-clinics',     icon: Stethoscope,    tagline: 'Reduce no-shows by 60%',               iconColor: 'text-emerald-600',  bgColor: 'bg-emerald-50 border-emerald-200' },
  { name: 'Real Estate',  path: '/ai-for-real-estate', icon: Home,           tagline: 'Capture every property inquiry',       iconColor: 'text-orange-600',   bgColor: 'bg-orange-50 border-orange-200' },
  { name: 'Dental',       path: '/ai-for-dental',      icon: Smile,          tagline: 'Patient scheduling on autopilot',      iconColor: 'text-cyan-600',     bgColor: 'bg-cyan-50 border-cyan-200' },
  { name: 'HVAC',         path: '/ai-for-hvac',        icon: Wind,           tagline: 'Service calls routed instantly',       iconColor: 'text-violet-600',   bgColor: 'bg-violet-50 border-violet-200' },
  { name: 'Salons',       path: '/ai-for-salons',      icon: Scissors,       tagline: 'Book appointments automatically',      iconColor: 'text-rose-600',     bgColor: 'bg-rose-50 border-rose-200' },
  { name: 'Fitness',      path: '/ai-for-fitness',     icon: Dumbbell,       tagline: 'Membership inquiries handled 24/7',    iconColor: 'text-lime-600',     bgColor: 'bg-lime-50 border-lime-200' },
];

const TESTIMONIALS = [
  { quote: "Harmonix handles 80% of our client inquiries automatically. Our booking rate increased by 40% in the first month!", author: "Dr. Sarah Chen", role: "Dental Clinic Owner", location: "Los Angeles, CA", rating: 5, img: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=80&h=80&fit=crop&crop=face' },
  { quote: "As a solo plumber, I was missing calls during jobs. Now Harmonix books appointments and qualifies leads 24/7.", author: "Mike Rodriguez", role: "Licensed Plumber", location: "Miami, FL", rating: 5, img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face' },
  { quote: "Our law firm needed compliant client intake. Harmonix qualifies cases and schedules consultations perfectly.", author: "Jennifer Walsh, Esq.", role: "Managing Partner", location: "Chicago, IL", rating: 5, img: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop&crop=face' },
];

const STATS = [
  { value: '90%', label: 'Faster Response', icon: MessageSquare, color: 'text-rose-500' },
  { value: '40%', label: 'More Bookings', icon: Calendar, color: 'text-amber-500' },
  { value: '24/7', label: 'Always Available', icon: Zap, color: 'text-teal-500' },
  { value: '3s', label: 'Avg Response Time', icon: Sparkles, color: 'text-violet-500' },
];

const TRUST_BADGES = [
  { icon: Shield, label: 'GDPR', color: 'text-sky-600 bg-sky-50 border-sky-200' },
  { icon: Lock, label: 'SOC 2', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  { icon: Heart, label: 'HIPAA', color: 'text-rose-500 bg-rose-50 border-rose-200' },
  { icon: Lock, label: '256-bit SSL', color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { icon: Zap, label: '99.9% Uptime', color: 'text-teal-600 bg-teal-50 border-teal-200' },
  { icon: Shield, label: 'ISO 27001', color: 'text-violet-500 bg-violet-50 border-violet-200' },
];

const FEATURES = [
  { icon: MessageSquare, title: '24/7 Instant Replies', desc: 'AI answers every message instantly, day or night.', color: 'bg-rose-50 text-rose-500' },
  { icon: Calendar, title: 'Smart Scheduling', desc: 'Syncs with Google Calendar. Clients book in real-time.', color: 'bg-emerald-50 text-emerald-600' },
  { icon: BarChart3, title: 'Lead Capture & CRM', desc: 'Every conversation becomes a scored lead in your pipeline.', color: 'bg-violet-50 text-violet-500' },
  { icon: Mail, title: 'Automated Follow-ups', desc: 'AI sends sequences to nurture leads and reduce no-shows.', color: 'bg-amber-50 text-amber-600' },
  { icon: Globe, title: 'Multi-Channel', desc: 'Website, WhatsApp, Facebook, Instagram, email -- one AI.', color: 'bg-sky-50 text-sky-500' },
  { icon: FileText, title: 'Policy-Aware', desc: 'Trained on your documents. Always accurate, always compliant.', color: 'bg-teal-50 text-teal-600' },
];

/* ─── Harmonix Chat Widget Embed ─── */
function HarmonixChatEmbed() {
  useEffect(() => {
    // Only load the script once
    if (document.getElementById('harmonix-widget-script')) return;

    // Fetch demo widget config (api_key) from backend before injecting script
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/demo-widget`)
      .then(res => res.json())
      .then(({ chatbot_id, api_key }) => {
        if (!chatbot_id || !api_key) return;
        // Avoid double-injection if mount fires twice
        if (document.getElementById('harmonix-widget-script')) return;
        const script = document.createElement('script');
        script.id = 'harmonix-widget-script';
        script.src = `/widget.js`;
        script.setAttribute('data-chatbot-id', chatbot_id);
        script.setAttribute('data-api-key', api_key);
        script.setAttribute('data-position', 'bottom-right');
        script.async = true;
        document.body.appendChild(script);
      })
      .catch(() => {/* widget load failure is non-critical */});

    return () => {
      const existingScript = document.getElementById('harmonix-widget-script');
      if (existingScript) existingScript.remove();
      const widget = document.getElementById('harmonix-chat-widget');
      if (widget) widget.remove();
    };
  }, []);

  return null; // The widget is injected via script
}

/* ─── Dynamic Pricing Section ─── */
function PricingSection({ plans }) {
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);
  const displayPlans = plans.filter(p => !p.is_custom).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  return (
    <section id="pricing" className="py-20 px-4 sm:px-6 scroll-mt-20" data-testid="homepage-pricing">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <Badge className="bg-teal-50 text-teal-700 border-teal-200 mb-4">Pricing</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Simple, transparent pricing</h2>
          <p className="text-base text-slate-500 max-w-xl mx-auto mb-6">Start free — no credit card required. Upgrade only when you&apos;re ready.</p>
          {/* Only show monthly/annual toggle when at least one plan actually has a monthly price. */}
          {displayPlans.some(p => (p.monthly_price || 0) > 0) && (
            <div className="inline-flex items-center bg-slate-100 rounded-full p-1">
              <button onClick={() => setAnnual(false)} className={`px-5 py-2 text-sm font-medium rounded-full transition-all ${!annual ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`} data-testid="pricing-toggle-monthly">Monthly</button>
              <button onClick={() => setAnnual(true)} className={`px-5 py-2 text-sm font-medium rounded-full transition-all ${annual ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`} data-testid="pricing-toggle-annual">Annual <span className="text-emerald-600 text-xs ml-1">Save 15%</span></button>
            </div>
          )}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '1.25rem' }}>
          {displayPlans.map((plan) => {
            const isOneTime = plan.billing_type === 'one_time' && plan.one_time_price > 0;
            const displayPrice = isOneTime
              ? Math.round(plan.one_time_price)
              : (annual && plan.annual_price ? (plan.annual_price / 12).toFixed(0) : Math.round(plan.monthly_price));
            const priceSuffix = isOneTime ? 'once' : '/mo';
            const ctaLabel = plan.is_free || (plan.monthly_price === 0 && !isOneTime) ? 'Start Free' : (isOneTime ? 'Claim Lifetime' : 'Get Started');
            return (
              <Card key={plan.plan_id} className={`relative w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(25%-1rem)] min-w-[250px] max-w-[320px] ${plan.is_popular ? 'border-2 border-teal-500 shadow-lg ring-1 ring-teal-100' : 'border-slate-200'}`} data-testid={`plan-card-${plan.name.toLowerCase().replace(/\s/g,'-')}`}>
                {plan.is_popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-teal-600 text-white text-xs px-3 py-0.5">Most Popular</Badge></div>}
                <CardContent className="p-6 h-full flex flex-col">
                  <h3 className="text-lg font-bold text-slate-900">{plan.name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5 mb-4">{plan.description}</p>
                  <div className="mb-5">
                    <span className="text-4xl font-extrabold text-slate-900">${displayPrice}</span>
                    <span className="text-sm text-slate-500"> {priceSuffix}</span>
                    {annual && plan.monthly_price > 0 && !isOneTime && <p className="text-xs text-emerald-600 mt-1">Billed ${plan.annual_price}/yr</p>}
                    {isOneTime && <p className="text-xs text-emerald-600 mt-1">Pay once — no monthly bill</p>}
                  </div>
                  <div className="space-y-2.5 mb-6 flex-1">
                    {(plan.features || []).map((f) => (
                      <div key={f} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={() => navigate('/login?signup=true')}
                    className={`w-full mt-auto ${plan.is_popular ? 'bg-teal-600 hover:bg-teal-700 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-800'}`}
                  >
                    {ctaLabel}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
        {/* Custom Plan */}
        <Card className="border-dashed border-2 border-teal-300 bg-teal-50/30" data-testid="custom-plan-card">
          <CardContent className="p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Need a custom plan?</h3>
              <p className="text-sm text-slate-500 max-w-lg">Design your own plan with our interactive builder. Choose exactly the features you need and pay only for what you use.</p>
            </div>
            <Button onClick={() => navigate('/pricing#custom-plan-builder')} className="bg-teal-600 hover:bg-teal-700 text-white whitespace-nowrap">
              Build Custom Plan <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

/* ─── Hero Carousel ─── */
function HeroCarousel({ navigate }) {
  const [index, setIndex] = useState(0);
  const total = HERO_SLIDES.length;

  // A/B test: assign and persist a variant so we can later re-introduce the
  // legacy hero and measure conversion lift. Currently single variant.
  const variant = useRef(getVariant('homepage_hero', ['v2-ai-employee'])).current;

  // Fire impression once per mount
  useEffect(() => {
    trackAbEvent('homepage_hero', variant, 'impression', { slides: total });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-rotate every 6.5s (always runs; manual nav resets the timer via effect re-run)
  useEffect(() => {
    const t = setInterval(() => setIndex(i => (i + 1) % total), 6500);
    return () => clearInterval(t);
  }, [total, index]);

  const goPrev = () => setIndex(i => (i - 1 + total) % total);
  const goNext = () => setIndex(i => (i + 1) % total);
  const goTo = (i) => setIndex(i);

  const slide = HERO_SLIDES[index];
  const SlideIcon = slide.overlayIcon;
  const EyebrowIcon = slide.eyebrowIcon;

  const handleCta = (label) => {
    trackAbEvent('homepage_hero', variant, 'cta_click', { label, slide_id: slide.id });
    if (/demo/i.test(label)) navigate('/contact?type=demo');
    else navigate('/login?signup=true');
  };

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-orange-50/60 via-white to-teal-50/40"
      data-testid="homepage-hero"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Copy column */}
          <div key={slide.id} className="animate-in fade-in slide-in-from-left-4 duration-500">
            {/* Trust badges (always visible) */}
            <div className="flex flex-wrap gap-2 mb-6" data-testid="homepage-trust-badges">
              {TRUST_BADGES.map((b) => (
                <div key={b.label} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${b.color}`}>
                  <b.icon className="w-3 h-3" />{b.label}
                </div>
              ))}
            </div>

            {/* Slide eyebrow */}
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold mb-4 ${slide.badgeColor}`} data-testid="hero-slide-eyebrow">
              <EyebrowIcon className="w-3.5 h-3.5" />
              {slide.eyebrow}
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.08] tracking-tight mb-6">
              {slide.title}{' '}
              <span className={`text-transparent bg-clip-text bg-gradient-to-r ${slide.accent}`}>{slide.titleHighlight}</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
              {slide.body}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Button
                size="lg"
                onClick={() => handleCta(slide.ctaPrimary)}
                className="bg-teal-600 hover:bg-teal-700 text-white font-bold text-base px-7 h-12 shadow-lg shadow-teal-200"
                data-testid="hero-cta-btn"
              >
                {slide.ctaPrimary}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => handleCta(slide.ctaSecondary)}
                className="text-base px-7 h-12 border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="hero-demo-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {slide.ctaSecondary}
              </Button>
            </div>
            <div className="flex items-center gap-5 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-teal-500" />No credit card</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-teal-500" />5-min setup</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-teal-500" />Cancel anytime</span>
            </div>
          </div>

          {/* Right: Image column */}
          <div className="hidden lg:block">
            <div key={slide.id + '-img'} className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 animate-in fade-in zoom-in-95 duration-500">
              <img
                src={slide.image}
                alt={slide.eyebrow}
                className="w-full object-cover aspect-[4/3] bg-slate-50"
                loading="eager"
              />
              <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-slate-100 p-3.5 max-w-[240px]">
                <div className="flex items-center gap-2.5 mb-1.5">
                  <div className="w-7 h-7 rounded-full bg-teal-50 flex items-center justify-center">
                    <SlideIcon className="w-3.5 h-3.5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-800">{slide.overlayLabel}</p>
                    <p className="text-[10px] text-slate-400">Just now</p>
                  </div>
                </div>
                <p className="text-xs text-slate-600">{slide.overlaySub}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Carousel Controls */}
        <div className="mt-10 flex items-center justify-between gap-4 max-w-7xl mx-auto">
          <button
            onClick={goPrev}
            className="hidden sm:flex w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-teal-300 transition-all items-center justify-center text-slate-600 hover:text-teal-600"
            aria-label="Previous slide"
            data-testid="hero-prev-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex-1 flex items-center justify-center gap-2">
            {HERO_SLIDES.map((s, i) => (
              <button
                key={s.id}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}: ${s.eyebrow}`}
                data-testid={`hero-dot-${i}`}
                className="group relative h-2 rounded-full overflow-hidden bg-slate-200 transition-all"
                style={{ width: i === index ? 48 : 18 }}
              >
                <span
                  className={`absolute inset-0 bg-gradient-to-r ${s.accent} transition-all`}
                  style={{
                    transform: i === index ? 'scaleX(1)' : 'scaleX(0)',
                    transformOrigin: 'left',
                    animation: i === index ? 'heroProgress 6.5s linear forwards' : 'none',
                  }}
                />
              </button>
            ))}
          </div>

          <button
            onClick={goNext}
            className="hidden sm:flex w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm hover:shadow-md hover:border-teal-300 transition-all items-center justify-center text-slate-600 hover:text-teal-600"
            aria-label="Next slide"
            data-testid="hero-next-btn"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes heroProgress {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
      `}</style>
    </section>
  );
}

/* ─── Live "2 AM demo" section — SOLUTION step of the LazyChat-style funnel.
       Renders an animated chat transcript to show Harmonix answering a real
       customer at 2 AM. Uses CSS + staggered state (no external deps) so it
       degrades gracefully if JS is slow. ─── */
function TwoAmDemoSection({ navigate }) {
  const [step, setStep] = useState(0);
  const SCRIPT = [
    { who: 'them', text: "Hi, do you take walk-ins tomorrow? My tooth is killing me." },
    { who: 'harmonix', text: "So sorry to hear that! I can fit you in tomorrow at 9:15 AM or 11:40 AM. Which works?" },
    { who: 'them', text: "9:15 works." },
    { who: 'harmonix', text: "Booked ✅ Confirmation sent to +1 (415) 555-0132. Feel better!" },
  ];

  useEffect(() => {
    if (step >= SCRIPT.length) return;
    const t = setTimeout(() => setStep((s) => s + 1), step === 0 ? 400 : 1300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  return (
    <section className="py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-teal-50/40" data-testid="homepage-two-am-demo">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Badge className="bg-teal-50 text-teal-700 border-teal-200 mb-4">Live at 2:14 AM</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 leading-tight">
            Watch Harmonix book a client while you sleep
          </h2>
          <p className="text-base text-slate-600 mb-6">
            Trained on your services, pricing, and calendar — Harmonix qualifies, replies, and books in seconds. No hallucinations, no scripts to write.
          </p>
          <div className="space-y-3 mb-6">
            {[
              { icon: Zap, text: '3-second response time' },
              { icon: Calendar, text: 'Books straight into Google Calendar' },
              { icon: Shield, text: 'Only says what your knowledge base supports' },
            ].map((f) => (
              <div key={f.text} className="flex items-center gap-2.5 text-sm text-slate-700">
                <div className="w-7 h-7 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
                  <f.icon className="w-4 h-4" />
                </div>
                {f.text}
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              size="lg"
              onClick={() => navigate('/login?signup=true')}
              className="bg-teal-600 hover:bg-teal-700 text-white font-bold text-base px-7 h-12"
              data-testid="two-am-start-free-btn"
            >
              Start Free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/contact?type=demo')}
              className="text-base px-7 h-12 border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="two-am-book-demo-btn"
            >
              Book a Demo
            </Button>
          </div>
        </div>

        {/* Animated chat transcript */}
        <div className="relative">
          <div className="absolute -top-3 left-4 z-10 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 text-white text-[11px] font-medium shadow-lg">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE · 2:14 AM
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-4 py-3 flex items-center gap-2 text-white">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-semibold">Sunset Dental · Harmonix AI</p>
                <p className="text-[11px] text-teal-100">Typically replies in seconds</p>
              </div>
            </div>
            <div className="p-4 space-y-3 min-h-[320px] bg-slate-50">
              {SCRIPT.slice(0, step).map((m, i) => (
                <div
                  key={`chat-${i}-${m.who}`}
                  className={`flex ${m.who === 'them' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
                >
                  <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${m.who === 'them' ? 'bg-slate-800 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm'}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {step < SCRIPT.length && (
                <div className="flex justify-start">
                  <div className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 rounded-bl-sm">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '120ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '240ms' }} />
                    </div>
                  </div>
                </div>
              )}
              {step >= SCRIPT.length && (
                <div className="flex justify-center pt-2">
                  <button
                    type="button"
                    onClick={() => setStep(0)}
                    className="text-xs text-slate-500 underline hover:text-teal-600"
                    data-testid="two-am-replay-btn"
                  >
                    Replay conversation
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Main Homepage ─── */
export default function HomePage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);

  useEffect(() => {
    api.get('/public/plans').then(r => setPlans(r.data || [])).catch(() => {});
    api.get('/case-studies/public?limit=6')
      .then(r => {
        const items = r.data?.items || [];
        // Prefer featured ones; fall back to most recent
        const featured = items.filter(i => i.is_featured);
        setFeaturedBlogs(featured.length >= 3 ? featured : items);
      })
      .catch(() => {});
    // Scroll to pricing if hash is #pricing
    if (window.location.hash === '#pricing') {
      setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 500);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Harmonix | AI Employee for Service Businesses - Never Miss a Client</title>
        <meta name="description" content="Meet your first AI employee. Harmonix works as a 24/7 AI receptionist, intake manager, and scheduling assistant across WhatsApp, website, email, and social media." />
        <link rel="canonical" href="https://harmonix.info/" />
        {/* SoftwareApplication schema — used by Google for rich snippets on
            SERPs and for AI answer engines to describe the product accurately. */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Harmonix",
          "applicationCategory": "BusinessApplication",
          "applicationSubCategory": "AI Receptionist / AI Employee",
          "operatingSystem": "Web",
          "url": "https://harmonix.info/",
          "description": "Harmonix is an AI Employee platform for service businesses — a 24/7 AI receptionist that answers WhatsApp, website, email, and social messages, qualifies leads, and books appointments.",
          "brand": { "@type": "Brand", "name": "Harmonix" },
          "offers": (plans && plans.length > 0)
            ? plans.map(p => ({
                "@type": "Offer",
                "name": p.name,
                "price": p.billing_type === 'one_time' ? (p.one_time_price || 0) : (p.monthly_price || 0),
                "priceCurrency": "USD",
                "url": "https://harmonix.info/pricing",
                "availability": "https://schema.org/InStock",
                ...(p.billing_type !== 'one_time' && p.monthly_price > 0 ? { "priceSpecification": { "@type": "UnitPriceSpecification", "billingIncrement": 1, "unitCode": "MON", "price": p.monthly_price, "priceCurrency": "USD" } } : {}),
              }))
            : [{ "@type": "Offer", "name": "Free", "price": 0, "priceCurrency": "USD", "url": "https://harmonix.info/pricing", "availability": "https://schema.org/InStock" }],
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "127",
          },
        })}</script>
        {/* Individual Product schemas per plan — lets Google index each plan
            as a distinct SKU (helps price/feature rich results). */}
        {plans && plans.length > 0 && plans.map((p) => (
          <script key={`schema-${p.plan_id}`} type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": `Harmonix ${p.name}`,
            "description": p.description || `${p.name} plan — Harmonix AI Employee`,
            "brand": { "@type": "Brand", "name": "Harmonix" },
            "offers": {
              "@type": "Offer",
              "price": p.billing_type === 'one_time' ? (p.one_time_price || 0) : (p.monthly_price || 0),
              "priceCurrency": "USD",
              "url": "https://harmonix.info/pricing",
              "availability": "https://schema.org/InStock",
            },
          })}</script>
        ))}
        {/* Organization / breadcrumb */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Harmonix",
          "url": "https://harmonix.info/",
          "logo": "https://harmonix.info/logo.png",
          "sameAs": [
            "https://www.linkedin.com/company/harmonix-ai",
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "sales",
            "url": "https://harmonix.info/contact",
            "availableLanguage": ["English"],
          },
        })}</script>
      </Helmet>

      {/* Promotional Popup */}
      <PromotionalPopup />

      <div className="min-h-screen bg-white" data-testid="homepage">
        <PublicHeader />

        {/* ─── Hero Carousel (ACTION-oriented CTA + trust) ─── */}
        <HeroCarousel navigate={navigate} />

        {/* ─── Section 1 · PROBLEM → CONSEQUENCE — LazyChat-style funnel opener ─── */}
        <section className="py-16 px-4 sm:px-6 bg-gradient-to-b from-white via-rose-50/30 to-white" data-testid="homepage-problem">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <Badge className="bg-rose-50 text-rose-700 border-rose-200 mb-4">The 2 AM problem</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 leading-tight">
                Every unanswered message is a customer walking to your competitor
              </h2>
              <p className="text-base text-slate-600 max-w-2xl mx-auto">
                71% of service-business inquiries arrive outside business hours. Miss them for 10 minutes and 40% of those leads are gone forever.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {[
                { icon: Clock, title: 'After-hours silence', body: 'A prospect messages you at 10 PM. You reply the next morning. They booked with a competitor at 10:03 PM.' },
                { icon: MessageSquare, title: 'Message fragmentation', body: 'WhatsApp, Instagram, email, website chat, missed calls — no single team member sees the full picture.' },
                { icon: TrendingUp, title: 'Slow-response tax', body: '3× drop in lead-to-booking rate for every 30 minutes of delay. Speed is the moat.' },
              ].map((c) => (
                <Card key={c.title} className="border-rose-100 bg-white/70 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center mb-4">
                      <c.icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-base font-semibold text-slate-900 mb-2">{c.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{c.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Section 2 · SOLUTION — Live "2 AM demo" ─── */}
        <TwoAmDemoSection navigate={navigate} />

        {/* ─── Section 3 · PROOF (stats bar) ─── */}
        <section className="border-y border-slate-200 bg-white py-10" data-testid="homepage-stats">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {STATS.map((s) => (
                <div key={s.value}>
                  <s.icon className={`w-5 h-5 mx-auto mb-2 ${s.color}`} />
                  <p className="text-3xl sm:text-4xl font-extrabold text-slate-900">{s.value}</p>
                  <p className="text-sm text-slate-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Features Grid ─── */}
        <section className="py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-amber-50/30" data-testid="homepage-features">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <Badge className="bg-amber-50 text-amber-700 border-amber-200 mb-4">Features</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Everything your AI employee can do</h2>
              <p className="text-base text-slate-500 max-w-2xl mx-auto">From the first client message to the booked appointment -- Harmonix handles it end-to-end.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {FEATURES.map((f) => (
                <Card key={f.title || f.name} className="border-slate-200 hover:border-teal-200 hover:shadow-md transition-all bg-white">
                  <CardContent className="p-6">
                    <div className={`w-10 h-10 rounded-xl ${f.color} flex items-center justify-center mb-4`}><f.icon className="w-5 h-5" /></div>
                    <h3 className="text-base font-semibold text-slate-900 mb-2">{f.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ─── ROI Comparison ─── */}
        <section className="py-20 px-4 sm:px-6" data-testid="homepage-roi">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <Badge className="bg-rose-50 text-rose-600 border-rose-200 mb-4">Value</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">The real cost of slow responses</h2>
              <p className="text-base text-slate-500">See how Harmonix compares to hiring a full-time assistant</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-slate-200">
                <CardContent className="p-8">
                  <p className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-2">Traditional Staff</p>
                  <p className="text-4xl font-extrabold text-slate-800 mb-6">$3,200<span className="text-base font-normal text-slate-400">/mo</span></p>
                  <div className="space-y-3">
                    {['$15-20/hr salary + benefits', 'Works 8 hours/day only', 'Messages missed on breaks & sick days', 'Training & onboarding costs', 'Limited availability'].map((item) => (
                      <div key={item} className="flex items-center gap-2.5 text-sm text-slate-500"><div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />{item}</div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card className="border-2 border-teal-400 shadow-lg ring-1 ring-teal-100">
                <CardContent className="p-8">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm font-medium text-teal-700 uppercase tracking-wide">Harmonix AI Employee</p>
                    <Badge className="bg-teal-600 text-white text-[10px]">Recommended</Badge>
                  </div>
                  <p className="text-4xl font-extrabold text-slate-900 mb-6">
                    {(() => {
                      // Use the cheapest paid plan; fall back to the Free plan tagline.
                      const paidPlans = plans.filter(p => p.monthly_price > 0);
                      if (paidPlans.length > 0) {
                        const cheapest = paidPlans.sort((a, b) => a.monthly_price - b.monthly_price)[0];
                        return <>${cheapest.monthly_price}<span className="text-base font-normal text-slate-400">/mo</span></>;
                      }
                      const oneTime = plans.find(p => p.one_time_price > 0);
                      if (oneTime) {
                        return <>${oneTime.one_time_price}<span className="text-base font-normal text-slate-400"> once</span></>;
                      }
                      return <>Free<span className="text-base font-normal text-slate-400"> to start</span></>;
                    })()}
                  </p>
                  <div className="space-y-3">
                    {['24/7/365 -- never takes a break', 'Handles unlimited messages', 'Instant 3-second response time', 'Works across all channels', 'Lead scoring & CRM built-in'].map((item) => (
                      <div key={item} className="flex items-center gap-2.5 text-sm text-slate-700"><Check className="w-4 h-4 text-teal-500 flex-shrink-0" />{item}</div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* ─── Industries - Cards with images ─── */}
        <section className="py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-sky-50/30" data-testid="homepage-industries">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="bg-sky-50 text-sky-700 border-sky-200 mb-4">Industries</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Built for service businesses</h2>
              <p className="text-base text-slate-500 max-w-2xl mx-auto">From emergency plumbing calls to legal intake — Harmonix adapts to your industry. Click any industry to see how.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {INDUSTRIES.map((ind) => (
                <Link
                  key={ind.path}
                  to={ind.path}
                  className="group relative rounded-2xl bg-white p-5 border border-slate-200 hover:border-transparent hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden"
                  data-testid={`industry-link-${ind.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {/* Hover gradient halo */}
                  <div className={`absolute -inset-px rounded-2xl bg-gradient-to-br ${ind.color} opacity-0 group-hover:opacity-100 transition-opacity -z-10`} aria-hidden="true" />
                  <div className={`absolute inset-0.5 rounded-2xl bg-white -z-10`} aria-hidden="true" />

                  <div className={`w-12 h-12 rounded-xl ${ind.bgColor} border flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <ind.icon className={`w-6 h-6 bg-clip-text bg-gradient-to-br ${ind.color}`} style={{ color: 'transparent', strokeWidth: 2 }} strokeLinecap="round" />
                    <ind.icon className={`w-6 h-6 absolute text-slate-700 group-hover:text-transparent transition-colors`} style={{ background: 'none' }} aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1.5 group-hover:text-slate-900">{ind.name}</h3>
                  <p className="text-sm text-slate-500 leading-snug mb-3">{ind.tagline}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 group-hover:text-teal-700 transition-colors">
                    See how it works
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/industries" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-100 hover:bg-teal-50 text-slate-700 hover:text-teal-700 text-sm font-semibold border border-slate-200 hover:border-teal-200 transition-colors" data-testid="see-all-industries">
                See all industries <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </section>

        {/* ─── Featured Blogs ─── */}
        {featuredBlogs.length > 0 && (
          <section className="py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-orange-50/40" data-testid="homepage-featured-blogs">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="bg-orange-50 text-orange-700 border-orange-200 mb-4">Featured Reads</Badge>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Insights from the front line</h2>
                <p className="text-base text-slate-500 max-w-2xl mx-auto">
                  Practical guides, case studies, and playbooks from teams running AI Employees in production.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredBlogs.slice(0, 3).map((post) => (
                  <Link
                    key={post.case_study_id}
                    to={`/case-studies/${post.slug || post.case_study_id}`}
                    className="group rounded-2xl bg-white border border-slate-200 hover:border-teal-300 hover:shadow-xl transition-all overflow-hidden flex flex-col"
                    data-testid={`featured-blog-${post.case_study_id}`}
                  >
                    {/* Cover image */}
                    {post.featured_image ? (
                      <div className="aspect-[16/9] overflow-hidden bg-slate-50">
                        <img
                          src={post.featured_image.startsWith('http') ? post.featured_image : `${process.env.REACT_APP_BACKEND_URL}${post.featured_image}`}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="aspect-[16/9] bg-gradient-to-br from-teal-50 via-cyan-50 to-orange-50 flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-teal-300" />
                      </div>
                    )}

                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-2.5">
                        <Badge className={post.post_type === 'case_study' ? 'bg-emerald-100 text-emerald-700 border-0 text-[10px]' : 'bg-orange-100 text-orange-700 border-0 text-[10px]'}>
                          {post.post_type === 'case_study' ? 'Case Study' : 'Blog'}
                        </Badge>
                        {post.industry && <span>· {post.industry}</span>}
                        {post.read_time && (
                          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{post.read_time}</span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 leading-snug mb-2 line-clamp-2 group-hover:text-teal-700 transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 mb-4">
                        {post.summary}
                      </p>
                      {(post.author_name || post.author_avatar) && (
                        <div className="flex items-center gap-2.5 mb-3 pb-3 border-b border-slate-100">
                          {post.author_avatar ? (
                            <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-100 flex-shrink-0" style={{ aspectRatio: '1 / 1' }}>
                              <img
                                src={post.author_avatar}
                                alt={post.author_name || 'Author'}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-500 flex-shrink-0">
                              {(post.author_name || 'H').charAt(0)}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-slate-900 truncate">{post.author_name || 'Harmonix Team'}</p>
                            {post.author_title && (
                              <p className="text-[10px] text-slate-500 truncate">{post.author_title}</p>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-teal-600 group-hover:text-teal-700">
                        Read more <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="text-center mt-10">
                <Link to="/case-studies" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-100 hover:bg-teal-50 text-slate-700 hover:text-teal-700 text-sm font-semibold border border-slate-200 hover:border-teal-200 transition-colors" data-testid="see-all-blogs">
                  Browse all reads <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* ─── Dynamic Pricing ─── */}
        {plans.length > 0 && <PricingSection plans={plans} />}

        {/* ─── Testimonials with images ─── */}
        <section className="py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-orange-50/30" data-testid="homepage-testimonials">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="bg-amber-50 text-amber-700 border-amber-200 mb-4">Testimonials</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Trusted by businesses worldwide</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {TESTIMONIALS.map((t, i) => (
                <Card key={t.name || i} className="border-slate-200 hover:shadow-md transition-shadow bg-white" data-testid={`testimonial-${i}`}>
                  <CardContent className="p-6">
                    <div className="flex gap-0.5 mb-4">{Array(t.rating).fill(0).map((_, j) => (<Star key={`${t.name || i}-star-${j}`} className="w-4 h-4 fill-amber-400 text-amber-400" />))}</div>
                    <p className="text-sm text-slate-600 leading-relaxed mb-5">&ldquo;{t.quote}&rdquo;</p>
                    <div className="border-t border-slate-100 pt-4 flex items-center gap-3">
                      <img src={t.img} alt={t.author} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                      <div><p className="text-sm font-semibold text-slate-900">{t.author}</p><p className="text-xs text-slate-400">{t.role} &middot; {t.location}</p></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Blog / Case Studies CTA ─── */}
        <section className="py-16 px-4 sm:px-6" data-testid="homepage-blog-cta">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Learn from real success stories</h2>
            <p className="text-base text-slate-500 mb-8 max-w-xl mx-auto">See how businesses like yours are using AI to grow revenue and delight clients.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/case-studies?type=case_study')} className="border-slate-300 text-slate-700 hover:border-teal-300 hover:text-teal-700">
                Read Case Studies <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/blog?type=blog')} className="border-slate-300 text-slate-700 hover:border-teal-300 hover:text-teal-700">
                Visit Our Blog <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* ─── Final CTA ─── */}
        <section className="py-20 px-4 sm:px-6 bg-gradient-to-br from-teal-600 to-cyan-600" data-testid="homepage-cta">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-5">
              Ready to hire your first AI employee?
            </h2>
            <p className="text-lg text-teal-100 mb-8 max-w-2xl mx-auto leading-relaxed">
              Join thousands of businesses that stopped losing clients to unanswered messages and slow responses.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/login?signup=true')}
                className="bg-white hover:bg-slate-50 text-teal-700 font-bold text-base px-8 h-12 shadow-lg"
                data-testid="cta-bottom-btn"
              >
                Start Free — No Credit Card
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/contact?type=demo')}
                className="text-base px-8 h-12 border-white/40 text-white hover:bg-white/10"
                data-testid="cta-bottom-demo-btn"
              >
                Book a Demo
              </Button>
            </div>
          </div>
        </section>

        <PublicFooter />
        {/* Harmonix Chat Widget - Real AI Employee */}
        <HarmonixChatEmbed />
      </div>
    </>
  );
}
